import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callEdgeFunction } from "@/lib/edge-functions";
import { supabase } from "@/lib/supabase";
import { buildKycQueue, isAwaitingReview } from "./kyc-queue";
import type { KycDecision, KycReviewRecord, KycSubmissionRecord } from "./kyc-queue";

/**
 * Client KYC review (0070).
 *
 * Reads are plain `.select()`s guarded by 0070's RLS and COLUMN grants — the
 * convention for simple reads, and it keeps one rule in one place: the database
 * is what withholds `storage_path`. Note what is therefore impossible here — no
 * query in this file can produce a document path, so no bug in this app can leak
 * one.
 *
 * The two writes go through Edge routes because both are authority-bearing and
 * audited: issuing a document link records who looked, and a decision is the
 * only path past `trg_profiles_kyc_status_audit`.
 */

const SUBMISSION_COLUMNS =
  "id, client_id, declared_document_type, mime_type, byte_size, selfie_byte_size, purged_at, created_at, " +
  "profiles!kyc_submissions_client_id_fkey ( full_name, phone, kyc_status )";

/** Newest first. Capped: this is a pilot queue, not an archive browser. */
export function useKycSubmissions() {
  return useQuery({
    queryKey: ["kyc-submissions"],
    queryFn: async (): Promise<KycSubmissionRecord[]> => {
      const { data, error } = await supabase
        .from("kyc_submissions")
        .select(SUBMISSION_COLUMNS)
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as unknown as KycSubmissionRecord[];
    },
    staleTime: 15_000,
  });
}

/**
 * Decisions for the same window. `reason` is selected because superadmin holds
 * the grant for it — it is the reviewer's own note, withheld from the client by
 * column grant, and showing it here is how a second reviewer learns why a
 * client was refused.
 */
export function useKycReviews() {
  return useQuery({
    queryKey: ["kyc-reviews"],
    queryFn: async (): Promise<(KycReviewRecord & { reason: string | null })[]> => {
      const { data, error } = await supabase
        .from("kyc_submission_reviews")
        .select("submission_id, decision, reason, created_at")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as (KycReviewRecord & { reason: string | null })[];
    },
    staleTime: 15_000,
  });
}

/**
 * The sidebar badge — how many submissions nobody has decided on yet.
 *
 * Composes the same two reads `KycReviewPage` already makes, under the same
 * query keys, so React Query dedupes the network call when both are mounted
 * at once rather than this being a second implementation of "what counts as
 * pending". `enabled` defaults true; AppSidebar passes
 * `role === "superadmin"` so the other four roles, who hold no grant on
 * either table (0070), never issue a request RLS would just empty-answer.
 */
export function useKycPendingCount(enabled = true) {
  return useKycAwaiting(enabled).count;
}

/**
 * The same queue as the sidebar badge, with what an attention item needs: the
 * oldest undecided submission, and whether the read failed — a failed read
 * must not look like an empty queue (Ops Dashboard strip, live map).
 */
export function useKycAwaiting(enabled = true) {
  const submissions = useQuery({
    queryKey: ["kyc-submissions"],
    queryFn: async (): Promise<KycSubmissionRecord[]> => {
      const { data, error } = await supabase
        .from("kyc_submissions")
        .select(SUBMISSION_COLUMNS)
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as unknown as KycSubmissionRecord[];
    },
    enabled,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
  const reviews = useQuery({
    queryKey: ["kyc-reviews"],
    queryFn: async (): Promise<(KycReviewRecord & { reason: string | null })[]> => {
      const { data, error } = await supabase
        .from("kyc_submission_reviews")
        .select("submission_id, decision, reason, created_at")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as (KycReviewRecord & { reason: string | null })[];
    },
    enabled,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const awaiting = submissions.data
    ? buildKycQueue(submissions.data, reviews.data ?? []).filter(isAwaitingReview)
    : [];
  const oldestAt =
    awaiting
      .map((row) => row.submittedAt)
      .filter((at): at is string => at !== null)
      .sort()[0] ?? null;
  return {
    count: awaiting.length,
    oldestAt,
    ready: Boolean(submissions.data && reviews.data),
    failed: submissions.isError || reviews.isError,
  };
}

/**
 * Mint a short-lived link to one document.
 *
 * Not a `useQuery`: the URL expires in 60 seconds, so caching it would hand a
 * reviewer a dead link on the second open. Every call writes a
 * `kyc_document_viewed` audit row — the record of who has ever seen a given
 * client's ID.
 */
/** Which image of a submission — the ID, or the selfie taken with it (BL-43). */
export type KycImage = "document" | "selfie";

export function useKycDocumentUrl() {
  return useMutation({
    mutationFn: (input: { clientId: string; submissionId: string; image: KycImage }) =>
      callEdgeFunction<{ url: string; expiresInSeconds: number }>(
        `admin/clients/${input.clientId}/kyc-document-url`,
        { data: { submissionId: input.submissionId, image: input.image } },
      ),
  });
}

export function useKycDecision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      clientId: string;
      submissionId: string;
      decision: KycDecision;
      reason?: string;
    }) =>
      callEdgeFunction<{ clientId: string; decision: KycDecision }>(
        `admin/clients/${input.clientId}/kyc-decision`,
        {
          data: {
            submissionId: input.submissionId,
            decision: input.decision,
            ...(input.reason ? { reason: input.reason } : {}),
          },
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["kyc-submissions"] });
      void queryClient.invalidateQueries({ queryKey: ["kyc-reviews"] });
    },
  });
}
